import {
	useRef,
	useEffect,
	PropsWithChildren,
} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import styles from './Drawer.module.scss';
import { Button, Spacer } from '@components';
import { BUTTON_SIZE, BUTTON_TYPES } from '@types';

type DrawerProps = {
	open: boolean;
	height?: string;
	canCloseWithSwipe?: boolean;
	isBackArrowVisible?: boolean;
	isFooterVisible?: boolean;
	footerCancelBtnText?: string;
	footerActionBtnText?: string;
	bgColor?: string;
	isLoading?: boolean;
	toggleDrawer: (b: boolean) => void;
	onBackArrowClick?: () => void;
	onCancelBtnClick?: () => void;
	onActionBtnClick?: () => void;
	footerChildren?: React.ReactNode;
}

export const Drawer = (props: PropsWithChildren<DrawerProps>) => {
	const {
		children,
		open = false,
		height = 'calc(100% - 65px)', // navbar
		canCloseWithSwipe = true,
		isBackArrowVisible = false,
		isFooterVisible = false,
		footerActionBtnText = 'Save',
		footerCancelBtnText = 'Cancel',
		bgColor = 'var(--neutral-00)',
		isLoading = false,
		footerChildren,
		toggleDrawer,
		onBackArrowClick,
		onActionBtnClick,
		onCancelBtnClick
	} = props;
	const closeDrawer = () => {
		toggleDrawer(false);
	}
	const drawerRef = useRef<HTMLDivElement>(null);
	const startY = useRef<number | null>(null);
	const currentY = useRef<number>(0);
	const isDragging = useRef(false);

	const handleTouchStart = (e: React.TouchEvent) => {
		if (!canCloseWithSwipe) return;
		startY.current = e.touches[0].clientY;
		isDragging.current = true;
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (!canCloseWithSwipe) return;
		if (!isDragging.current || startY.current === null) return;
		currentY.current = e.touches[0].clientY;

		const deltaY = currentY.current - startY.current;
		if (deltaY > 0 && drawerRef.current) {
			drawerRef.current.style.transform = `translateY(${deltaY}px)`;
		}
	};

	const handleTouchEnd = () => {
		if (!canCloseWithSwipe) return;
		if (!isDragging.current || startY.current === null) return;
		const deltaY = currentY.current - startY.current;

		const threshold = 60;
		if (deltaY > threshold) {
			closeDrawer();
		} else if (drawerRef.current) {
			drawerRef.current.style.transform = 'translateY(0)';
		}

		isDragging.current = false;
		startY.current = null;
		currentY.current = 0;
	};

	useEffect(() => {
		// Reset transform on open
		if (open && drawerRef.current) {
			drawerRef.current.style.transform = 'translateY(0)';
			return
		}
		if (!open && drawerRef.current) {
			drawerRef.current.style.transform = 'translateY(100%)';
		}
	}, [open]);

	return (
		<div
			data-testid={'@Ofload-Drawer'}
			className={`${styles.drawerWrapper} ${open && styles.open}`}
		>
			{open && <div className={styles.drawerBackdrop} onClick={closeDrawer} />}
			<div
				ref={drawerRef}
				className={`
					${styles.drawerContainer}
					${open && styles.open}
				`}
				style={{
					minHeight: height,
					backgroundColor: bgColor
				}}
			>
				<div
					className={styles.drawerHandleContainer}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
				>
					<div className={`${styles.drawerHandle} ${!canCloseWithSwipe && styles.drawerHandleHidden}`} />
				</div>
				<div className={styles.drawerHeading}>
					{
						isBackArrowVisible ?
							<button onClick={onBackArrowClick} className={styles.iconButton} aria-label={`Go back`}>
								<ArrowBackIcon />
							</button> : <span />
					}
					<button onClick={closeDrawer} className={styles.iconButton} aria-label={`Close`} data-testid={'@Ofload-Drawer-Close-Btn'}>
						<CloseIcon />
					</button>
				</div>
				<Spacer size={8} />
				<div className={`${styles.drawerContent} ${isFooterVisible && styles.drawerContentWithFooter}`}>
					{children}
				</div>
				{
					isFooterVisible &&
					<footer className={styles.drawerFooter}>
						{ footerChildren }
						{
							onCancelBtnClick && <>
								<Button
									text={footerCancelBtnText}
									onClick={() => onCancelBtnClick?.()}
									type={BUTTON_TYPES.TERTIARY}
									disabled={isLoading}
								/>
							</>
						}
						{
							onActionBtnClick &&
							<Button
								text={footerActionBtnText}
								onClick={() => onActionBtnClick?.()}
								type={BUTTON_TYPES.PRIMARY}
								size={BUTTON_SIZE.SMALL}
								loading={isLoading}
							/>
						}
					</footer>
				}
			</div>
		</div>
	);
}
